import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { loadTranslations } from '@angular/localize';
import { firstValueFrom } from 'rxjs';
import { Language } from '@vendasta/galaxy/i18n';

export const BOOKME_LANGUAGES: Language[] = Object.values(Language);

const TRANSLATION_FETCH_TIMEOUT_MS = 2500;

declare const $localize: { locale?: string };

let resolvedBookmeLocale: string | undefined;

// Matching semantics mirror the platform's findLanguageFromCode plus the exact-short-code
// preference used by the email service's unsubscribe page (es-ES -> es, not es-419),
// kept local so this app does not require platform lib changes.
export function resolveBookmeLocale(): string {
  if (resolvedBookmeLocale) {
    return resolvedBookmeLocale;
  }
  resolvedBookmeLocale = resolveBrowserLocale() ?? Language.ENGLISH.toString();
  return resolvedBookmeLocale;
}

export function resetResolvedBookmeLocale(): void {
  resolvedBookmeLocale = undefined;
}

function matchSupportedLanguage(code: string): string | undefined {
  const exactFull = BOOKME_LANGUAGES.find((lang) => lang.toString() === code);
  if (exactFull) {
    return exactFull.toString();
  }
  const prefix = BOOKME_LANGUAGES.find((lang) => lang.toString().startsWith(code));
  if (prefix) {
    return prefix.toString();
  }
  if (code.indexOf('-') >= 2) {
    const short = code.slice(0, 2);
    const exactShort = BOOKME_LANGUAGES.find((lang) => lang.toString() === short);
    if (exactShort) {
      return exactShort.toString();
    }
    return BOOKME_LANGUAGES.find((lang) => lang.toString().startsWith(short))?.toString();
  }
  return undefined;
}

function resolveBrowserLocale(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const raw = (navigator.languages?.[0] || navigator.language || '').toLowerCase().replace(/_/g, '-');
  return raw ? matchSupportedLanguage(raw) : undefined;
}

@Injectable({ providedIn: 'root' })
export class LocalizeBootstrapService {
  private readonly translate = inject(TranslateService);

  async init(): Promise<void> {
    const locale = resolveBookmeLocale();
    this.translate.use(locale);
    $localize.locale = locale;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }

    if (locale === Language.ENGLISH) {
      return;
    }

    try {
      const translations = await Promise.race([firstValueFrom(this.translate.getTranslation(locale)), timeout()]);
      if (!translations) {
        return;
      }
      const flat = this.flatten(translations as Record<string, unknown>);
      if (Object.keys(flat).length > 0) {
        loadTranslations(flat);
      }
    } catch {
      return;
    }
  }

  private flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
    const out: Record<string, string> = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      const path = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object') {
        Object.assign(out, this.flatten(value as Record<string, unknown>, path));
      } else {
        out[path] = String(value);
      }
    }
    return out;
  }
}

function timeout(): Promise<undefined> {
  return new Promise((resolve) => setTimeout(() => resolve(undefined), TRANSLATION_FETCH_TIMEOUT_MS));
}
